/**
 * ClientDashboard
 * ---------------
 * Mobile-first home for parents + adult clients. Per Sachin's product spec:
 *   - Tabs at the top: PARENT first, then one tab per athlete on the account.
 *   - Sessions are SHARED across the family (pool badge on top).
 *   - Subscriptions can be per-athlete (each athlete tab has its own Subscribe).
 *   - Card on file lives on the parent tab; any tab's checkout uses it.
 *   - Adult-self accounts (single "athlete" matching parent name) skip the tab
 *     bar entirely (Q4.a) — the original single-view layout still ships.
 *
 * Owner/coach routes elsewhere; this component is rendered by Dashboard.jsx
 * only when role === "client" && effectiveProfileComplete && !athlete_mode_for.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
    CalendarDays, Trash2, Plus, FileSignature, CreditCard,
    Sparkles, ChevronRight, Activity, Wallet, Share2, MessageCircle,
    Loader2, User as UserIcon, Users, BadgeCheck, RefreshCw, X, Lock,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ProfileCompletionBanner from "@/components/profile/ProfileCompletionBanner";
import MemberAlertsStrip from "@/components/MemberAlertsStrip";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDay = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, {
            weekday: "long", month: "short", day: "numeric",
        });
    } catch { return iso; }
};
const fmtTime = (t) => {
    if (!t) return "";
    try {
        const [h, m] = t.split(":");
        const hh = parseInt(h, 10);
        const ap = hh >= 12 ? "PM" : "AM";
        const h12 = ((hh + 11) % 12) + 1;
        return `${h12}:${m} ${ap}`;
    } catch { return t; }
};

// Cached gym timezone. Populated by fetchGymTimezone() on first call
// (fired from ClientDashboard mount). Falls back to Pacific if the
// fetch hasn't landed yet — matches the backend default.
let _CACHED_GYM_TZ = "America/Los_Angeles";
export async function fetchGymTimezone() {
    try {
        const r = await axios.get(`${API}/site/timezone`);
        if (r.data?.timezone) _CACHED_GYM_TZ = r.data.timezone;
    } catch {
        // keep the cached fallback
    }
    return _CACHED_GYM_TZ;
}

/**
 * Hours from now until the slot's start, interpreted as the gym's local
 * wall-clock time. The gym timezone is DB-driven (Sachin 2026-03-05).
 * Returns +Infinity if inputs are missing so the UI defaults to
 * "unlocked".
 */
function hoursUntilSession(dateStr, timeStr) {
    if (!dateStr || !timeStr) return Infinity;
    try {
        const tz = _CACHED_GYM_TZ;
        const [y, mo, d] = dateStr.split("-").map((n) => parseInt(n, 10));
        const [hh, mm] = timeStr.split(":").map((n) => parseInt(n, 10));
        // 1. First guess: treat wall-clock as UTC.
        const guess = Date.UTC(y, mo - 1, d, hh, mm, 0);
        // 2. Compute the tz offset by asking Intl what the same UTC
        //    instant looks like when rendered in the target tz.
        const localAsSeenInTz = new Date(guess).toLocaleString("en-US", { timeZone: tz });
        const localGuess = new Date(localAsSeenInTz).getTime();
        const offsetMs = guess - localGuess;
        const slotMs = guess + offsetMs;
        if (Number.isNaN(slotMs)) return Infinity;
        return (slotMs - Date.now()) / 3_600_000;
    } catch {
        return Infinity;
    }
}

export default function ClientDashboard({ user: authUser }) {
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchProfile = useCallback(async () => {
        try {
            const r = await axios.get(`${API}/profile`, { withCredentials: true });
            setProfile(r.data);
        } catch (e) {
            toast.error("Couldn't load your dashboard", {
                description: e?.response?.data?.detail || "Try refreshing the page.",
            });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        // Defer kickoff one microtask so react-hooks/set-state-in-effect
        // doesn't flag the entry (actual setStates run after await anyway).
        Promise.resolve().then(fetchProfile);
        // Hydrate the gym timezone cache so hoursUntilSession() uses the
        // right offset (Sachin 2026-03-05 tz work).
        fetchGymTimezone();
    }, [fetchProfile]);

    // Merge enriched /profile data over the AuthContext user. Guarded with
    // empty fallback so hooks below run on first render too.
    const user = { ...authUser, ...(profile || {}) };
    const firstName = (user.parent_first_name || (user.name || "Athlete").split(" ")[0]).trim();
    const athletes = user.athletes || [];
    const isAdultSelf = athletes.length === 1 && athletes[0].first_name === firstName;
    const totalRemaining = user.credits_total_remaining ?? null;
    const upcoming = user.upcoming_bookings || [];
    const last30 = user.sessions_last_30d ?? 0;

    const [activeTab, setActiveTab] = useState("parent");

    if (loading || !profile) {
        return (
            <div data-testid="client-dashboard-loading" className="min-h-screen bg-zinc-950 flex items-center justify-center">
                <Loader2 className="w-7 h-7 text-brand-red animate-spin" />
            </div>
        );
    }

    // Adult-self: skip the tab bar entirely (Q4.a) and render the single-view
    // dashboard exactly as it was before tabs landed.
    if (isAdultSelf) {
        return (
            <SingleViewDashboard
                user={user}
                firstName={firstName}
                upcoming={upcoming}
                last30={last30}
                totalRemaining={totalRemaining}
                onRefresh={fetchProfile}
            />
        );
    }

    return (
        <main data-testid="client-dashboard" className="min-h-screen bg-zinc-950 text-white pb-16">
            <header className="px-5 pt-7 pb-3">
                <div className="flex items-baseline justify-between gap-4">
                    <h1 className="text-2xl md:text-3xl font-semibold">Hi {firstName}</h1>
                    <Link
                        to="/dashboard/profile"
                        className="text-[10px] tracking-[0.2em] uppercase text-zinc-400 hover:text-white"
                        data-testid="open-profile"
                    >
                        Manage profile →
                    </Link>
                </div>
                <p className="text-zinc-400 text-sm mt-1">
                    {user.status === "member" ? "Member · MaxSC" : "Welcome back"}
                </p>
            </header>

            {/* Membership Status hero (Sachin 2026-02-28): always front
                and center showing sessions left + when the subscription
                runs out, with a "View options" CTA that explains what
                happens at the end of the period. Hidden gracefully if
                the member has no sub / no sessions yet. */}
            <div className="px-5 mt-4">
                <MemberAlertsStrip onChange={fetchProfile} />
            </div>

            {/* Shared family-pool session badge (Q5.a) — sits above tabs so it
                is visible no matter which tab is selected. Sessions are NEVER
                tab-specific; subscriptions are. */}
            <SessionPoolBadge total={totalRemaining} packs={user.active_packs} />

            <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-3">
                <div className="px-5">
                    <TabsList
                        data-testid="family-tabs"
                        className="w-full justify-start overflow-x-auto bg-zinc-900/60 border border-zinc-800 p-1 h-auto"
                    >
                        <TabsTrigger
                            value="parent"
                            data-testid="tab-parent"
                            className="data-[state=active]:bg-brand-red data-[state=active]:text-white text-zinc-300 text-xs"
                        >
                            <UserIcon className="w-3.5 h-3.5 mr-1.5" />
                            {firstName} (You)
                        </TabsTrigger>
                        {athletes.map((a) => (
                            <TabsTrigger
                                key={a.athlete_id}
                                value={a.athlete_id}
                                data-testid={`tab-athlete-${a.athlete_id}`}
                                className="data-[state=active]:bg-brand-red data-[state=active]:text-white text-zinc-300 text-xs"
                            >
                                {a.first_name}
                            </TabsTrigger>
                        ))}
                    </TabsList>
                </div>

                <TabsContent value="parent" className="mt-4 px-5">
                    <ParentTabContent
                        user={user}
                        firstName={firstName}
                        athletes={athletes}
                        onRefresh={fetchProfile}
                    />
                </TabsContent>

                {athletes.map((a) => (
                    <TabsContent key={a.athlete_id} value={a.athlete_id} className="mt-4 px-5">
                        <AthleteTabContent
                            athlete={a}
                            user={user}
                            upcoming={upcoming.filter((b) => b.athlete_id === a.athlete_id)}
                            onRefresh={fetchProfile}
                        />
                    </TabsContent>
                ))}
            </Tabs>

            {last30 > 0 && (
                <section className="mx-5 mt-6">
                    <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-2">
                        Family activity (last 30 days)
                    </div>
                    <div className="border border-zinc-800 bg-zinc-900/40 rounded-lg px-4 py-3 flex items-center gap-3">
                        <Activity className="w-5 h-5 text-emerald-400" />
                        <p className="text-sm text-zinc-300">
                            {last30} session{last30 === 1 ? "" : "s"} completed across the family. Keep it up!
                        </p>
                    </div>
                </section>
            )}
        </main>
    );
}


/* ─────────────────────────── Shared family-pool badge ─────────────────────── */

function SessionPoolBadge({ total, packs }) {
    const remaining = total ?? 0;
    const expiringPack = (packs || []).find((p) => p.expires_at);
    const colorClass =
        remaining > 4 ? "text-brand-red border-brand-red/40 bg-brand-red/10"
        : remaining > 0 ? "text-amber-300 border-amber-700/60 bg-amber-950/30"
        : "text-zinc-400 border-zinc-700 bg-zinc-900/40";
    return (
        <section
            data-testid="session-pool-badge"
            className={`mx-5 mt-2 border rounded-lg px-4 py-3 flex items-center justify-between ${colorClass}`}
        >
            <div className="flex items-center gap-3 min-w-0">
                <Users className="w-4 h-4 flex-shrink-0" />
                <div className="min-w-0">
                    <div className="text-[10px] tracking-[0.2em] uppercase opacity-80">
                        Family session pool
                    </div>
                    <div className="text-lg font-bold leading-tight">
                        {remaining} <span className="text-xs font-normal opacity-80">{remaining === 1 ? "session" : "sessions"} shared</span>
                    </div>
                    {expiringPack && (
                        <div className="text-[10px] opacity-70 mt-0.5">
                            {expiringPack.label || "Pack"} expires {fmtDay((expiringPack.expires_at + "").split("T")[0])}
                        </div>
                    )}
                </div>
            </div>
            <Link
                to="/dashboard/billing"
                data-testid="pool-buy-more"
                className="text-[10px] tracking-[0.2em] uppercase border border-current px-3 py-2 rounded hover:bg-white/5 transition-colors flex-shrink-0"
            >
                <Plus className="w-3 h-3 inline mr-1" />
                {remaining > 0 ? "Buy more" : "Buy a pack"}
            </Link>
        </section>
    );
}


/* ─────────────────────────── Parent tab content ─────────────────────────── */

function ParentTabContent({ user, firstName, athletes, onRefresh }) {
    // Sachin 2026-03-06 (round 4): accept any positive waiver signal.
    // See SingleViewDashboard for the same pattern + rationale.
    const hasWaiver = Boolean(
        user.self_waiver_signed
        || user.waiver?.signed_at
        || user.waiver_signed_at
    );
    const hasCard = Boolean(user.has_card_on_file);
    const sub = user.active_subscription;
    // Are they a paying / comp member? Drives the prominent badge + whether
    // the Workouts button is shown front-and-center. Sachin 2026-02-27:
    // "Make sure ALL profiles show they are a member... and easy access to
    // the buttons for scheduling and workouts."
    const isMember = Boolean(
        user.family_has_active_sub
        || user.status === "member"
        || user.comp_access
    );
    // Family-wide upcoming (already on the user object via /profile).
    const upcomingAll = user.upcoming_bookings || [];

    const quickActions = useMemo(() => {
        const items = [];
        if (!hasWaiver) items.push({
            key: "waiver", icon: FileSignature, label: "Sign your waiver",
            sub: "Required before your next session", href: "/welcome/waiver", urgent: true,
        });
        items.push({
            key: "card", icon: CreditCard,
            label: hasCard ? "Manage card on file" : "Add a card on file",
            sub: hasCard ? "Update or replace your card" : "Charged for any athlete's pack or subscription",
            href: "/dashboard/billing",
            urgent: !hasCard,
        });
        items.push({
            key: "messages", icon: MessageCircle, label: "Message your coach",
            sub: "Send a quick note or question",
            href: "/dashboard?tab=messages",
        });
        items.push({
            key: "buy", icon: Wallet, label: "Buy a family pack",
            sub: "Sessions go into the shared pool",
            href: "/dashboard/billing",
        });
        return items;
    }, [hasWaiver, hasCard]);

    return (
        <div data-testid="parent-tab-content" className="space-y-4">
            {/* Member status badge — prominent, front-and-center for paying
                families. Replaces the subtle "Member · MaxSC" subtitle on the
                page header so subscribers immediately see they're active. */}
            {isMember && (
                <div
                    data-testid="parent-member-badge"
                    className="border-2 border-emerald-500/50 bg-gradient-to-br from-emerald-900/30 to-emerald-950/40 rounded-xl px-4 py-3 flex items-center gap-3"
                >
                    <div className="w-10 h-10 rounded-full bg-emerald-500/20 border border-emerald-400/50 flex items-center justify-center flex-shrink-0">
                        <BadgeCheck className="w-5 h-5 text-emerald-300" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="text-[10px] tracking-[0.25em] uppercase text-emerald-300/90">Active Member</div>
                        <div className="text-sm text-white font-semibold">
                            {sub?.plan_name || (athletes.length > 0 ? `${athletes.length}-athlete family on MaxSC` : "Maximum Sports Conditioning")}
                        </div>
                    </div>
                </div>
            )}

            {/* Big-button shortcut row — Schedule + Workouts + Billing. The
                three things a member opens 90% of the time. Workouts shows
                only when the family actually has access (active sub). */}
            <div
                data-testid="parent-shortcut-row"
                className={`grid ${isMember ? "grid-cols-3" : "grid-cols-2"} gap-2`}
            >
                <ShortcutTile
                    testid="shortcut-schedule"
                    to="/dashboard/schedule"
                    icon={CalendarDays}
                    label="Schedule"
                    accent="red"
                />
                {isMember && (
                    <ShortcutTile
                        testid="shortcut-workouts"
                        to="/dashboard/workouts"
                        icon={Sparkles}
                        label="Workouts"
                        accent="navy"
                    />
                )}
                <ShortcutTile
                    testid="shortcut-billing"
                    to="/dashboard/billing"
                    icon={CreditCard}
                    label="Billing"
                    accent="zinc"
                />
            </div>

            {/* This week's schedule — only when there's something on the cal.
                Mirrors what coaches see on the dossier, but trimmed to the
                4 nearest sessions across all athletes on this account. */}
            {upcomingAll.length > 0 && (
                <div data-testid="parent-this-week">
                    <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-2 flex items-center justify-between">
                        <span>Coming up this week</span>
                        <Link
                            to="/dashboard/schedule"
                            className="text-zinc-500 hover:text-white text-[10px]"
                            data-testid="this-week-see-all"
                        >
                            See all →
                        </Link>
                    </div>
                    <div className="space-y-1.5">
                        {upcomingAll.slice(0, 4).map((b) => {
                            const ath = athletes.find((a) => a.athlete_id === b.athlete_id);
                            return (
                                <div
                                    key={b.booking_id}
                                    data-testid={`parent-upcoming-${b.booking_id}`}
                                    className="flex items-center justify-between gap-3 px-3 py-2.5 border border-zinc-800 bg-zinc-900/40 rounded-md"
                                >
                                    <div className="min-w-0 flex-1">
                                        <div className="text-sm text-white">
                                            {fmtDay(b.date)} · <span className="text-zinc-400">{fmtTime(b.start_time)}</span>
                                        </div>
                                        <div className="text-[11px] text-zinc-500 truncate">
                                            {ath ? ath.first_name : "Athlete"}{b.label ? ` · ${b.label}` : ""}
                                        </div>
                                    </div>
                                    <CalendarDays className="w-4 h-4 text-zinc-600 flex-shrink-0" />
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Complete-your-profile nudge (auto-hides when nothing missing) */}
            <ProfileCompletionBanner user={user} onUpdated={onRefresh} />

            {/* Account-level subscription chip (if any) */}
            {sub && !sub.athlete_id && (
                <SubscriptionChip sub={sub} label="Account subscription" />
            )}

            {/* Account info card */}
            <div className="border border-zinc-800 bg-zinc-900/40 rounded-lg px-4 py-3">
                <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-1">Account</div>
                <div className="text-sm text-white font-medium">{user.name || firstName}</div>
                <div className="text-xs text-zinc-400 mt-0.5">{user.email}</div>
                {athletes.length > 0 && (
                    <div className="text-[11px] text-zinc-500 mt-2">
                        {athletes.length} athlete{athletes.length === 1 ? "" : "s"} on this account
                    </div>
                )}
            </div>

            <div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-2">
                    Quick actions
                </div>
                <div className="space-y-2">
                    {quickActions.map((q) => (
                        <QuickActionRow key={q.key} {...q} />
                    ))}
                </div>
            </div>

            <ReferAFriendButton email={user.email} />
        </div>
    );
}


/* ShortcutTile — big-button used in the Parent tab's primary shortcut row
   (Schedule / Workouts / Billing). Mobile-first, finger-friendly tap target. */
function ShortcutTile({ testid, to, icon: Icon, label, accent }) {
    const accentCls =
        accent === "red"  ? "border-brand-red/40 hover:border-brand-red bg-brand-red/10 hover:bg-brand-red/20 text-brand-red" :
        accent === "navy" ? "border-blue-500/40 hover:border-blue-400 bg-blue-500/10 hover:bg-blue-500/20 text-blue-300" :
                            "border-zinc-700 hover:border-zinc-500 bg-zinc-900/40 hover:bg-zinc-900/60 text-zinc-300";
    return (
        <Link
            to={to}
            data-testid={testid}
            className={`flex flex-col items-center justify-center gap-1.5 py-4 rounded-xl border-2 transition-colors active:scale-[0.98] ${accentCls}`}
        >
            <Icon className="w-5 h-5" />
            <span className="text-[11px] tracking-[0.18em] uppercase font-semibold">{label}</span>
        </Link>
    );
}


/* ─────────────────────────── Athlete tab content ─────────────────────────── */

function AthleteTabContent({ athlete, user, upcoming, onRefresh }) {
    const subs = user.active_subscriptions || [];
    const athleteSub = subs.find((s) => s.athlete_id === athlete.athlete_id);
    const next = upcoming[0] || null;
    const hasCard = Boolean(user.has_card_on_file);

    return (
        <div data-testid={`athlete-tab-content-${athlete.athlete_id}`} className="space-y-4">
            {/* Athlete header */}
            <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                    <div className="text-lg font-semibold text-white truncate">
                        {athlete.first_name} {athlete.last_name || ""}
                    </div>
                    <div className="text-[11px] text-zinc-500 uppercase tracking-wider mt-0.5">
                        {athlete.track || athlete.grade || "Athlete"}
                    </div>
                </div>
                <Link
                    to={`/dashboard/athletes/${athlete.athlete_id}`}
                    data-testid={`athlete-profile-link-${athlete.athlete_id}`}
                    className="text-[10px] tracking-[0.2em] uppercase text-zinc-400 hover:text-white"
                >
                    Profile →
                </Link>
            </div>

            {/* Subscription status (per-athlete) */}
            {athleteSub ? (
                <SubscriptionChip
                    sub={athleteSub}
                    label={`${athlete.first_name}'s subscription`}
                    onManage="/dashboard/billing"
                />
            ) : (
                <Link
                    to={`/dashboard?tab=messages&topic=${encodeURIComponent(`Subscribe ${athlete.first_name}`)}`}
                    data-testid={`subscribe-athlete-${athlete.athlete_id}`}
                    className="block border border-dashed border-zinc-700 hover:border-brand-red/60 rounded-lg px-4 py-3 transition-colors"
                >
                    <div className="text-sm font-medium text-white">
                        Subscribe {athlete.first_name}
                    </div>
                    <div className="text-[11px] text-zinc-500 mt-0.5">
                        Message your coach — they&apos;ll set up a monthly plan tagged to {athlete.first_name}. Sessions go to the shared family pool.
                    </div>
                </Link>
            )}

            {/* Next session for this athlete */}
            <div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-2">
                    {athlete.first_name}&apos;s next session
                </div>
                {next ? (
                    <NextSessionCard booking={next} onCancelled={onRefresh} />
                ) : (
                    <div
                        data-testid={`no-upcoming-${athlete.athlete_id}`}
                        className="border border-dashed border-zinc-800 rounded-xl px-5 py-6 text-center"
                    >
                        <CalendarDays className="w-7 h-7 text-zinc-600 mx-auto mb-2" />
                        <p className="text-sm text-zinc-300">
                            Nothing scheduled for {athlete.first_name} yet.
                        </p>
                        <Link
                            to={`/dashboard/schedule?athlete_id=${encodeURIComponent(athlete.athlete_id)}`}
                            data-testid={`book-athlete-${athlete.athlete_id}`}
                            className="mt-3 inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase border border-brand-red bg-brand-red text-white px-3 py-2 rounded hover:bg-brand-red/90 transition-colors"
                        >
                            <Plus className="w-3 h-3" /> Book a session
                        </Link>
                    </div>
                )}
                {upcoming.length > 1 && (
                    <div className="mt-3 space-y-2">
                        <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">
                            Coming up ({upcoming.length - 1} more)
                        </div>
                        {upcoming.slice(1, 4).map((b) => (
                            <div
                                key={b.booking_id}
                                className="border border-zinc-800 bg-zinc-900/30 rounded-md px-3 py-2 text-sm flex items-center justify-between"
                                data-testid={`upcoming-row-${b.booking_id}`}
                            >
                                <div>
                                    <span className="text-white">{fmtDay(b.date)}</span>
                                    <span className="text-zinc-500 ml-2">{fmtTime(b.start_time)}</span>
                                </div>
                                <span className="text-[10px] text-zinc-500 uppercase tracking-wider">
                                    {b.label || "Session"}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Athlete-specific quick actions */}
            <div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-2">
                    Quick actions
                </div>
                <div className="space-y-2">
                    <QuickActionRow
                        key="book"
                        icon={CalendarDays}
                        label={`Book a session for ${athlete.first_name}`}
                        sub="Picks from the shared session pool"
                        href={`/dashboard/schedule?athlete_id=${encodeURIComponent(athlete.athlete_id)}`}
                    />
                    {athleteSub && (
                        <QuickActionRow
                            key="workout"
                            icon={Sparkles}
                            label={`Run ${athlete.first_name}'s workout`}
                            sub="Open the workout runner on this device"
                            href={`/dashboard/workouts?athlete_id=${encodeURIComponent(athlete.athlete_id)}`}
                        />
                    )}
                    {!hasCard && (
                        <QuickActionRow
                            key="card"
                            icon={CreditCard}
                            label="Add card on file"
                            sub={`Needed before subscribing ${athlete.first_name}`}
                            href="/dashboard/billing"
                            urgent
                        />
                    )}
                </div>
            </div>
        </div>
    );
}


/* ─────────────────────────── Shared atoms ─────────────────────────── */

function SubscriptionChip({ sub, label, onManage }) {
    return (
        <div
            data-testid="subscription-chip"
            className="border border-emerald-700/60 bg-emerald-900/20 rounded-lg px-4 py-3 flex items-start gap-3"
        >
            <BadgeCheck className="w-4 h-4 text-emerald-300 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
                <div className="text-[10px] tracking-[0.25em] uppercase text-emerald-300">{label}</div>
                <div className="text-sm text-white font-medium mt-0.5">
                    {sub.plan_name || "Active subscription"}
                </div>
                {sub.current_period_end && (
                    <div className="text-[11px] text-emerald-200/80 mt-0.5">
                        {sub.cancel_at_period_end ? "Ends" : "Renews"}{" "}
                        {fmtDay((sub.current_period_end + "").split("T")[0])}
                    </div>
                )}
            </div>
            {onManage && (
                <Link
                    to={onManage}
                    className="text-[10px] tracking-[0.2em] uppercase text-emerald-200 hover:text-white"
                >
                    Manage →
                </Link>
            )}
        </div>
    );
}

function QuickActionRow({ icon: Icon, label, sub, href, urgent }) {
    return (
        <Link
            to={href}
            data-testid={`quick-action-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className={`flex items-center gap-3 px-4 py-3 rounded-lg border transition-colors ${
                urgent
                    ? "border-amber-700/60 bg-amber-950/30 hover:bg-amber-900/40"
                    : "border-zinc-800 bg-zinc-900/40 hover:border-zinc-700"
            }`}
        >
            <Icon className={`w-4 h-4 flex-shrink-0 ${urgent ? "text-amber-300" : "text-zinc-400"}`} />
            <div className="flex-1 min-w-0">
                <div className={`text-sm font-medium ${urgent ? "text-amber-100" : "text-white"}`}>
                    {label}
                </div>
                <div className="text-[11px] text-zinc-500 truncate">{sub}</div>
            </div>
            <ChevronRight className="w-4 h-4 text-zinc-500 flex-shrink-0" />
        </Link>
    );
}

function ReferAFriendButton({ email }) {
    const handle = () => {
        const url = `${window.location.origin}/?ref=${encodeURIComponent(email || "maxsc")}`;
        const text = `Come train with me at Maximum Sports Conditioning — check it out: ${url}`;
        if (navigator.share) {
            navigator.share({ title: "MaxSC", text, url }).catch(() => {});
        } else {
            navigator.clipboard?.writeText(url);
            toast.success("Referral link copied", { description: url });
        }
    };
    return (
        <button
            type="button"
            data-testid="refer-a-friend"
            onClick={handle}
            className="w-full flex items-center gap-3 px-4 py-3 border border-zinc-800 bg-zinc-900/40 hover:border-brand-red/60 rounded-lg transition-colors text-left"
        >
            <Share2 className="w-4 h-4 text-brand-red flex-shrink-0" />
            <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-white">Refer a friend</div>
                <div className="text-[11px] text-zinc-500">Share MaxSC with another parent or athlete</div>
            </div>
            <ChevronRight className="w-4 h-4 text-zinc-500" />
        </button>
    );
}


/* ─────────────────────────── Next-session card (with cancel) ─────────────── */

function NextSessionCard({ booking, onCancelled }) {
    const [busy, setBusy] = useState(false);
    const [rescheduleOpen, setRescheduleOpen] = useState(false);
    // Sachin 2026-03-05: iOS Safari (home-screen PWA install) sometimes
    // swallows window.confirm() silently, leaving parents thinking the
    // Cancel button is broken. Replaced with an in-app confirm modal.
    const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
    // Lock-state for the change-deadline chip + button disabled-states.
    // Mirrors backend rules in routes/schedule.py:
    //   • <1h  → both cancel and reschedule blocked (409)
    //   • 1–12h → reschedule blocked; cancel allowed but counts as a late
    //             cancel (2/mo = 1 paid session deducted)
    //   • >12h → both freely allowed, credit refunds normally
    const hoursLeft = hoursUntilSession(booking.date, booking.start_time);
    const isHardLocked = hoursLeft < 1;
    const isLateLocked = hoursLeft >= 1 && hoursLeft < 12;
    const doCancel = async () => {
        setConfirmCancelOpen(false);
        setBusy(true);
        try {
            const r = await axios.delete(
                `${API}/schedule/bookings/${booking.booking_id}`,
                { withCredentials: true },
            );
            const msg = r.data?.message || "Your session has been cancelled.";
            if (r.data?.penalty) {
                toast.error("Session cancelled — credit deducted", { description: msg });
            } else if (r.data?.late_cancel) {
                toast.warning("Session cancelled — late cancel logged", { description: msg });
            } else {
                toast.success("Session cancelled", { description: msg });
            }
            onCancelled?.();
        } catch (e) {
            toast.error("Couldn't cancel", { description: e?.response?.data?.detail || "Try again or contact your coach." });
        } finally {
            setBusy(false);
        }
    };
    return (
        <div
            data-testid="next-session-card"
            className="border border-emerald-700/40 bg-emerald-900/10 rounded-xl px-4 py-4"
        >
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="text-lg font-semibold text-white">{fmtDay(booking.date)}</div>
                    <div className="text-sm text-zinc-300 mt-0.5">
                        {fmtTime(booking.start_time)} · {booking.label || "Session"}
                    </div>
                    <div className="text-[11px] text-zinc-500 mt-1 space-x-2">
                        {booking.athlete_first_name && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-300">
                                <UserIcon className="w-3 h-3" />
                                {booking.athlete_first_name}
                            </span>
                        )}
                        {booking.duration_minutes && (
                            <span>· {booking.duration_minutes} min</span>
                        )}
                    </div>
                    {/* Change-deadline chip: only renders inside the lock window
                        so a normal upcoming session stays clean. */}
                    {(isHardLocked || isLateLocked) && (
                        <span
                            data-testid={`lock-chip-${booking.booking_id}`}
                            className={`mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded text-[10px] tracking-[0.15em] uppercase ${
                                isHardLocked
                                    ? "bg-rose-950/40 border border-rose-700/60 text-rose-200"
                                    : "bg-amber-950/40 border border-amber-700/60 text-amber-200"
                            }`}
                            title={
                                isHardLocked
                                    ? "Inside 1h of start — nothing can change here. Contact your coach."
                                    : "Inside 12h — you can still cancel (counts as a late cancel) but can't reschedule."
                            }
                        >
                            <Lock className="w-3 h-3" />
                            {isHardLocked
                                ? "Locked · session starts soon"
                                : "Change window closed"}
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-0.5 flex-shrink-0">
                    <button
                        type="button"
                        onClick={() => setRescheduleOpen(true)}
                        disabled={busy || isHardLocked || isLateLocked}
                        data-testid={`next-session-reschedule-${booking.booking_id}`}
                        className="text-zinc-500 hover:text-brand-red disabled:opacity-30 disabled:cursor-not-allowed transition-colors p-1.5"
                        title={
                            isHardLocked || isLateLocked
                                ? "Reschedule locked — within 12 hours of start"
                                : "Move this session to a different time"
                        }
                        aria-label="Reschedule session"
                    >
                        <RefreshCw className="w-4 h-4" />
                    </button>
                    <button
                        type="button"
                        onClick={() => setConfirmCancelOpen(true)}
                        disabled={busy || isHardLocked}
                        data-testid="next-session-cancel"
                        className="text-zinc-500 hover:text-rose-400 disabled:opacity-30 disabled:cursor-not-allowed transition-colors p-1.5"
                        title={
                            isHardLocked
                                ? "Cancel locked — within 1 hour of start"
                                : isLateLocked
                                    ? "Cancel allowed but counts as a late cancel under our policy"
                                    : "Cancel this session"
                        }
                        aria-label="Cancel session"
                    >
                        <Trash2 className="w-4 h-4" />
                    </button>
                </div>
            </div>
            {rescheduleOpen && (
                <RescheduleModal
                    booking={booking}
                    onClose={() => setRescheduleOpen(false)}
                    onRescheduled={() => {
                        setRescheduleOpen(false);
                        onCancelled?.();
                    }}
                />
            )}
            {confirmCancelOpen && (
                <ConfirmCancelModal
                    booking={booking}
                    isLateLocked={isLateLocked}
                    onClose={() => setConfirmCancelOpen(false)}
                    onConfirm={doCancel}
                />
            )}
        </div>
    );
}


/**
 * ConfirmCancelModal — in-app confirmation replacing window.confirm().
 * Sachin 2026-03-05: iOS Safari PWA sometimes swallowed window.confirm,
 * making parents think the Cancel button was broken.
 */
function ConfirmCancelModal({ booking, isLateLocked, onClose, onConfirm }) {
    return (
        <div
            data-testid="confirm-cancel-modal"
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-4"
            onClick={onClose}
        >
            <div
                className="bg-zinc-950 border border-zinc-800 rounded-t-2xl md:rounded-2xl w-full max-w-md p-6"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="text-[10px] tracking-[0.25em] uppercase text-rose-400 mb-2 inline-flex items-center gap-1.5">
                    <Trash2 className="w-3 h-3" />
                    Cancel session?
                </div>
                <h3 className="text-lg font-semibold text-white leading-snug">
                    {fmtDay(booking.date)} · {fmtTime(booking.start_time)}
                </h3>
                <p className="text-sm text-zinc-300 mt-3 leading-relaxed">
                    {isLateLocked
                        ? "This is inside 12 hours of your session — it counts as a late cancel. Two late cancels in a calendar month deducts 1 paid session."
                        : "You'll get your session credit back."}
                </p>
                <div className="mt-6 flex flex-col-reverse sm:flex-row justify-end gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="confirm-cancel-keep"
                        className="px-4 py-2.5 text-[11px] tracking-[0.15em] uppercase border border-zinc-700 text-zinc-200 hover:border-zinc-500 rounded"
                    >
                        Keep session
                    </button>
                    <button
                        type="button"
                        onClick={onConfirm}
                        data-testid="confirm-cancel-yes"
                        className="px-4 py-2.5 text-[11px] tracking-[0.15em] uppercase bg-rose-600 hover:bg-rose-700 text-white rounded"
                    >
                        Yes, cancel it
                    </button>
                </div>
            </div>
        </div>
    );
}


/**
 * RescheduleModal — picks a new slot for an existing booking and POSTs to
 * /api/schedule/bookings/{bid}/reschedule. Loads the next 14 days of
 * available slots, filters out the booking's current slot, and only shows
 * non-full ones the athlete isn't already booked into.
 *
 * Sachin 2026-02-27 — parents need self-serve reschedule, not just cancel.
 */
function RescheduleModal({ booking, onClose, onRescheduled }) {
    const [slots, setSlots] = useState(null);
    const [busy, setBusy] = useState(false);
    const [pickedId, setPickedId] = useState(null);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                // Pull the next 14 days of open slots. /schedule/slots returns
                // only future, non-cancelled rows so we filter client-side
                // for capacity + skipping the current slot.
                const today = new Date();
                const to = new Date(today);
                to.setDate(to.getDate() + 14);
                const fromDate = today.toISOString().slice(0, 10);
                const toDate = to.toISOString().slice(0, 10);
                const r = await axios.get(`${API}/schedule/slots`, {
                    params: { from_date: fromDate, to_date: toDate },
                    withCredentials: true,
                });
                const list = (r.data || []).filter((s) =>
                    s.slot_id !== booking.slot_id
                    && (s.capacity > (s.booked_count || 0))
                );
                if (alive) setSlots(list);
            } catch (e) {
                if (alive) {
                    setSlots([]);
                    toast.error("Couldn't load available slots", {
                        description: e?.response?.data?.detail,
                    });
                }
            }
        })();
        return () => { alive = false; };
    }, [booking.slot_id]);

    const confirm = async () => {
        if (!pickedId) return;
        setBusy(true);
        try {
            const r = await axios.post(
                `${API}/schedule/bookings/${booking.booking_id}/reschedule`,
                { new_slot_id: pickedId },
                { withCredentials: true },
            );
            toast.success("Session rescheduled", {
                description: r.data?.message || "Your new time is confirmed.",
            });
            onRescheduled?.();
        } catch (e) {
            toast.error("Couldn't reschedule", {
                description: e?.response?.data?.detail || "Try again or contact your coach.",
            });
        } finally {
            setBusy(false);
        }
    };

    // Group slots by date for a tidy day-by-day list.
    const byDate = (slots || []).reduce((acc, s) => {
        (acc[s.date] = acc[s.date] || []).push(s);
        return acc;
    }, {});
    const sortedDates = Object.keys(byDate).sort();

    return (
        <div
            data-testid="reschedule-modal"
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-4"
            onClick={onClose}
        >
            <div
                className="bg-zinc-950 border border-zinc-800 rounded-t-2xl md:rounded-2xl w-full max-w-lg max-h-[85vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                <header className="flex items-center justify-between px-5 py-4 border-b border-zinc-800 flex-shrink-0">
                    <div className="min-w-0">
                        <div className="text-[10px] tracking-[0.25em] uppercase text-brand-red">Reschedule</div>
                        <div className="text-sm text-white font-semibold mt-0.5 truncate">
                            From {fmtDay(booking.date)} · {fmtTime(booking.start_time)}
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="reschedule-modal-close"
                        className="text-zinc-400 hover:text-white p-1"
                        aria-label="Close"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </header>
                <div className="flex-1 overflow-y-auto px-5 py-4">
                    {slots === null ? (
                        <div className="py-12 flex items-center justify-center">
                            <Loader2 className="w-6 h-6 text-brand-red animate-spin" />
                        </div>
                    ) : sortedDates.length === 0 ? (
                        <div data-testid="reschedule-no-slots" className="py-10 text-center">
                            <CalendarDays className="w-7 h-7 text-zinc-700 mx-auto mb-2" />
                            <p className="text-sm text-zinc-300">No open slots in the next 14 days.</p>
                            <p className="text-[11px] text-zinc-500 mt-1">
                                Message your coach — they can squeeze you in or open a new slot.
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {sortedDates.map((d) => (
                                <div key={d}>
                                    <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-2">
                                        {fmtDay(d)}
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                        {byDate[d].map((s) => {
                                            const picked = pickedId === s.slot_id;
                                            const remaining = Math.max(0, (s.capacity || 0) - (s.booked_count || 0));
                                            return (
                                                <button
                                                    key={s.slot_id}
                                                    type="button"
                                                    data-testid={`reschedule-slot-${s.slot_id}`}
                                                    onClick={() => setPickedId(s.slot_id)}
                                                    className={`text-left px-3 py-2.5 rounded-lg border-2 transition-colors ${
                                                        picked
                                                            ? "border-brand-red bg-brand-red/15 text-white"
                                                            : "border-zinc-800 bg-zinc-900/40 hover:border-zinc-600 text-zinc-200"
                                                    }`}
                                                >
                                                    <div className="text-sm font-semibold">
                                                        {fmtTime(s.start_time)}
                                                    </div>
                                                    <div className="text-[10px] text-zinc-500 mt-0.5">
                                                        {s.label || (s.category || "Session")} · {remaining} spot{remaining === 1 ? "" : "s"} left
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <footer className="px-5 py-4 border-t border-zinc-800 flex items-center justify-between gap-3 flex-shrink-0">
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-[11px] tracking-[0.2em] uppercase text-zinc-400 hover:text-white"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={confirm}
                        disabled={!pickedId || busy}
                        data-testid="reschedule-confirm"
                        className="btn-tactical text-xs disabled:opacity-40"
                    >
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                        Move session
                    </button>
                </footer>
            </div>
        </div>
    );
}


/* ─────────────────────────── Adult-self single view (Q4.a) ────────────────── */
/**
 * Adult solo clients (single "athlete" who IS the parent) see the original
 * tab-less dashboard layout. Mirrors the pre-tabs UX exactly.
 */
function SingleViewDashboard({ user, firstName, upcoming, last30, totalRemaining, onRefresh }) {
    const next = upcoming[0] || null;
    const sub = user.active_subscription;
    const packs = user.active_packs || [];
    // Sachin 2026-03-06 (round 4): waiver check accepts ANY positive
    // signal — new `self_waiver_signed`, nested `waiver.signed_at`, or
    // legacy top-level `waiver_signed_at`. Guards against mixed
    // old-JS / new-API states on mobile.
    const hasWaiver = Boolean(
        user.self_waiver_signed
        || user.waiver?.signed_at
        || user.waiver_signed_at
    );
    const hasCard = Boolean(user.has_card_on_file);
    const isMember = Boolean(
        user.family_has_active_sub
        || user.status === "member"
        || user.comp_access
        || sub
    );

    const quickActions = useMemo(() => {
        const items = [];
        if (!hasWaiver) items.push({ key: "waiver", icon: FileSignature, label: "Sign your waiver", sub: "Required before your next session", href: "/welcome/waiver", urgent: true });
        if (!hasCard) items.push({ key: "card", icon: CreditCard, label: "Add a card on file", sub: "So you can book sessions in one tap", href: "/dashboard/billing", urgent: true });
        if (sub) items.push({ key: "workout", icon: Sparkles, label: "Run today's workout", sub: "Open the workout runner on this device", href: "/dashboard/workouts" });
        items.push({ key: "messages", icon: MessageCircle, label: "Message your coach", sub: "Send a quick note or question", href: "/dashboard?tab=messages" });
        items.push({ key: "buy", icon: Wallet, label: "Buy more sessions", sub: totalRemaining != null ? `${totalRemaining} left` : "View packs", href: "/dashboard/billing" });
        return items;
    }, [hasWaiver, hasCard, totalRemaining, sub]);

    return (
        <main data-testid="client-dashboard-self" className="min-h-screen bg-zinc-950 text-white pb-16">
            <header className="px-5 pt-7 pb-5">
                <div className="flex items-baseline justify-between gap-4">
                    <h1 className="text-2xl md:text-3xl font-semibold">Hi {firstName}</h1>
                    <Link to="/dashboard/profile" className="text-[10px] tracking-[0.2em] uppercase text-zinc-400 hover:text-white">Manage profile →</Link>
                </div>
                <p className="text-zinc-400 text-sm mt-1">
                    {user.status === "member" ? "Member · MaxSC" : "Welcome back"}
                </p>
            </header>

            {/* Member badge — same prominent treatment as the parent tab. */}
            {isMember && (
                <section
                    data-testid="self-member-badge"
                    className="mx-5 mb-3 border-2 border-emerald-500/50 bg-gradient-to-br from-emerald-900/30 to-emerald-950/40 rounded-xl px-4 py-3 flex items-center gap-3"
                >
                    <div className="w-10 h-10 rounded-full bg-emerald-500/20 border border-emerald-400/50 flex items-center justify-center flex-shrink-0">
                        <BadgeCheck className="w-5 h-5 text-emerald-300" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="text-[10px] tracking-[0.25em] uppercase text-emerald-300/90">Active Member</div>
                        <div className="text-sm text-white font-semibold">
                            {sub?.plan_name || "Maximum Sports Conditioning"}
                        </div>
                    </div>
                </section>
            )}

            {/* Big-button shortcut row — Schedule + Workouts + Billing. */}
            <section
                data-testid="self-shortcut-row"
                className={`mx-5 mb-4 grid ${isMember ? "grid-cols-3" : "grid-cols-2"} gap-2`}
            >
                <ShortcutTile testid="self-shortcut-schedule" to="/dashboard/schedule" icon={CalendarDays} label="Schedule" accent="red" />
                {isMember && (
                    <ShortcutTile testid="self-shortcut-workouts" to="/dashboard/workouts" icon={Sparkles} label="Workouts" accent="navy" />
                )}
                <ShortcutTile testid="self-shortcut-billing" to="/dashboard/billing" icon={CreditCard} label="Billing" accent="zinc" />
            </section>

            {/* Complete-your-profile nudge — adult-self version. Auto-hides when filled. */}
            <ProfileCompletionBanner user={user} onUpdated={onRefresh} />

            <section data-testid="hero-card" className="mx-5 mb-5 border border-zinc-800 bg-gradient-to-br from-brand-red/10 to-zinc-900/40 rounded-xl px-5 py-7 text-center">
                {sub && (
                    <div data-testid="hero-sub-chip" className="inline-flex items-center gap-1.5 mb-3 px-3 py-1 rounded-full border border-emerald-700/60 bg-emerald-900/30 text-emerald-200 text-[10px] tracking-[0.2em] uppercase">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                        {sub.plan_name || "Active subscription"}
                        {sub.current_period_end && (
                            <span className="text-emerald-300/80 normal-case tracking-normal">
                                · {sub.cancel_at_period_end ? "ends" : "renews"} {fmtDay((sub.current_period_end + "").split("T")[0])}
                            </span>
                        )}
                    </div>
                )}
                {totalRemaining != null && totalRemaining > 0 ? (
                    <>
                        <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-2">Sessions left</div>
                        <div data-testid="hero-session-count" className="text-5xl md:text-6xl font-bold text-brand-red leading-none">{totalRemaining}</div>
                        {packs[0]?.expires_at && (
                            <div className="text-sm text-zinc-400 mt-3">
                                {packs[0].label || "Active pack"} · expires {fmtDay((packs[0].expires_at + "").split("T")[0])}
                            </div>
                        )}
                        <Link to="/dashboard/billing" data-testid="hero-buy-more" className="mt-5 inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase border border-brand-red bg-brand-red text-white px-4 py-2.5 rounded hover:bg-brand-red/90 transition-colors">
                            <Plus className="w-3 h-3" /> Buy more sessions
                        </Link>
                    </>
                ) : (
                    <>
                        <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-2">
                            {sub ? "Sessions left" : "No active sessions"}
                        </div>
                        <div className="text-lg text-zinc-300">{sub ? "0 · subscription keeps you training" : "Ready to get started?"}</div>
                        {!sub && (
                            <Link to="/dashboard/billing" data-testid="hero-start" className="mt-5 inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase border border-brand-red bg-brand-red text-white px-4 py-2.5 rounded hover:bg-brand-red/90 transition-colors">
                                <Wallet className="w-3 h-3" /> Browse packs & memberships
                            </Link>
                        )}
                    </>
                )}
            </section>

            <section className="mx-5 mb-5">
                <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-2">Your next session</div>
                {next ? (
                    <NextSessionCard booking={next} onCancelled={onRefresh} />
                ) : (
                    <div data-testid="next-session-empty" className="border border-dashed border-zinc-800 rounded-xl px-5 py-6 text-center">
                        <CalendarDays className="w-7 h-7 text-zinc-600 mx-auto mb-2" />
                        <p className="text-sm text-zinc-300">No upcoming sessions on the schedule.</p>
                        <p className="text-xs text-zinc-500 mt-1">Your coach will reach out to schedule.</p>
                    </div>
                )}
            </section>

            <section className="mx-5 mb-5">
                <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-2">Quick actions</div>
                <div className="space-y-2">
                    {quickActions.map((q) => <QuickActionRow key={q.key} {...q} />)}
                </div>
            </section>

            {last30 > 0 && (
                <section className="mx-5 mb-5">
                    <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-2 flex items-center justify-between">
                        <span>Recent activity</span>
                        <span className="text-emerald-400 font-mono normal-case tracking-normal">
                            {last30} session{last30 === 1 ? "" : "s"} in last 30 days
                        </span>
                    </div>
                    <div className="border border-zinc-800 bg-zinc-900/40 rounded-lg px-4 py-3 flex items-center gap-3">
                        <Activity className="w-5 h-5 text-emerald-400" />
                        <p className="text-sm text-zinc-300">You&apos;re on a roll. Keep it up!</p>
                    </div>
                </section>
            )}

            <section className="mx-5 mb-5">
                <ReferAFriendButton email={user.email} />
            </section>
        </main>
    );
}
